import { createBrowserRouter, Navigate } from 'react-router-dom';

/**
 * Role-aware shells.
 *
 * This tier RENDERS. It holds no business logic and makes no authorization
 * decisions — the shell a user sees is a convenience, not a security boundary.
 * The API decides what data comes back. See ADR-0003.
 */
export const router = createBrowserRouter([
  { path: '/', lazy: () => import('../features/shell/RootShell') },
  /**
   * The student's own surface, through the SAME shell as /college.
   *
   * It used to be its own bare page, so the sidebar — and every college
   * planning screen with it — existed only under /college and nothing linked
   * there. A student signing in could not reach any of it.
   */
  {
    path: '/student',
    lazy: () => import('../features/student/college/CollegeShell'),
    children: [
      { index: true, lazy: () => import('../features/student/StudentShell') },
    ],
  },
  /**
   * College planning is a SECTION, not a tab.
   *
   * The current product gives it seven sibling screens under one sidebar entry,
   * and flattening those into tabs inside StudentWorkspace would make every one
   * of them unlinkable — a counselor cannot send "look at your college list" as
   * a URL. Nested routes keep each screen addressable. See docs/16.
   */
  {
    path: '/college',
    lazy: () => import('../features/student/college/CollegeShell'),
    children: [
      { index: true, lazy: () => import('../features/student/college/PlanningHub') },
      // `:step?` rather than seven routes: the step list lives in wizard-steps.ts
      // and a router that enumerated them again would be a second copy to drift.
      { path: 'profile/:step?', lazy: () => import('../features/student/college/ProfileWizard') },
      // The discoverable door to the resume import. The panels that do the work
      // live on three wizard steps; a student who never opened one of those had
      // no way to learn the feature was there.
      { path: 'resume', lazy: () => import('../features/student/college/ResumeScreen') },
      // Minting a link releases data out of the school, so it lives behind its
      // own screen with ADR-0024's disclaimer on it rather than on the hub.
      { path: 'share', lazy: () => import('../features/student/college/ShareScreen') },
      { path: 'colleges', lazy: () => import('../features/student/college/CollegesScreen') },
      { path: 'positioning', lazy: () => import('../features/student/college/PositioningScreen') },
      { path: 'majors', lazy: () => import('../features/student/college/MajorsScreen') },
      { path: 'activities', lazy: () => import('../features/student/college/ActivitiesScreen') },
      { path: 'shortlist', lazy: () => import('../features/student/college/ShortlistScreen') },
    ],
  },
  /**
   * APPLICATIONS IS A TOP-LEVEL CATEGORY, through the same shell.
   *
   * It was `/college/applications`, a seventh step of the planning wizard's
   * sidebar. Planning is the work of deciding and applying is the work of
   * delivering against somebody else's dates; burying the half with deadlines
   * under the half without them is the wrong shape. Same shell, so the sidebar,
   * sign-in and student context are unchanged.
   */
  /**
   * THE STUDENT'S THREAD WITH THEIR COUNSELOR (dc-zh55), through the same shell
   * and a top-level entry of its own: it is about the whole of their plan, not
   * one section of it.
   */
  {
    path: '/messages',
    lazy: () => import('../features/student/college/CollegeShell'),
    children: [
      { index: true, lazy: () => import('../features/student/college/MessagesScreen') },
    ],
  },
  /**
   * CAREER PLANNING IS A TOP-LEVEL SECTION (dc-mios.4, EX-OD5), beside College
   * Planning and College Application, through the same shell. Explore is its
   * first screen; the quiz, my clusters and plans join it as they are built.
   * Each cluster is its own URL, so a counselor can send one.
   */
  {
    path: '/careers',
    lazy: () => import('../features/student/college/CollegeShell'),
    children: [
      { index: true, lazy: () => import('../features/student/careers/ExploreScreen') },
      { path: 'clusters/:id', lazy: () => import('../features/student/careers/ClusterScreen') },
      // The quiz and its result (dc-mios.6, QZ-OD4).
      { path: 'quiz', lazy: () => import('../features/student/careers/QuizScreen') },
      { path: 'results', lazy: () => import('../features/student/careers/ResultsScreen') },
      // One attempt reopened from the history, with its own fit report (dc-mios.7).
      {
        path: 'results/:attemptId',
        lazy: async () => ({ Component: (await import('../features/student/careers/ResultsScreen')).AttemptScreen }),
      },
      // My clusters (dc-mios.8): saved, starred, removed, added.
      { path: 'mine', lazy: () => import('../features/student/careers/MyClustersScreen') },
      // High school plans (dc-mios.10): the list, the builder, one plan.
      { path: 'plans', lazy: () => import('../features/student/careers/plans/PlansScreen') },
      { path: 'plans/new', lazy: () => import('../features/student/careers/plans/PlanWizard') },
      { path: 'plans/:planId', lazy: () => import('../features/student/careers/plans/PlanScreen') },
      // Where a plan review's "answered" notification leads (dc-mios.11).
      { path: 'plans/reviews/:reviewId', lazy: () => import('../features/student/careers/plans/PlanReview') },
    ],
  },
  /** The student's meetings with their counselor (dc-97jw), beside Messages. */
  {
    path: '/meetings',
    lazy: () => import('../features/student/college/CollegeShell'),
    children: [
      { index: true, lazy: () => import('../features/student/college/MeetingsScreen') },
    ],
  },
  {
    path: '/applications',
    lazy: () => import('../features/student/college/CollegeShell'),
    children: [
      { index: true, lazy: () => import('../features/student/college/ApplicationsScreen') },
      { path: 'letters', lazy: () => import('../features/student/college/LettersScreen') },
    ],
  },
  /**
   * A SHARED RECORD, OUTSIDE EVERY SHELL.
   *
   * Deliberately not nested under one: all four shells ask `useAuth` and render
   * a sign-in when there is no session, and the entire point of a share link is
   * reaching somebody who will never have one. Nesting this anywhere would show
   * a grandparent a sign-in form.
   *
   * The token is a path segment because that is what a link is. It stops being
   * one at the API boundary: the request puts it in a POST body, where a query
   * string would have put it in an access log and a Referer header.
   */
  { path: '/share/:token', lazy: () => import('../features/share/SharedView') },
  /**
   * OLD ROLE PATHS, SENT HOME (PROD-G05).
   *
   * `/counselor/*` was a placeholder page and `/school/*` and `/district/*` drew
   * the analytics dashboard with nothing linking to them. The workspace a person
   * gets is chosen at `/` from the role the server resolved, so a path naming a
   * role is a second way in that can disagree with it. Redirected rather than
   * dropped, so an old bookmark lands somewhere useful instead of a blank page.
   */
  ...['/counselor/*', '/school/*', '/district/*'].map((path) => ({
    path, element: <Navigate to="/" replace />,
  })),
]);
